import React from 'react';
import { useNavigate } from 'react-router-dom';
import { XIcon, CalendarIcon, DollarSignIcon, UsersIcon, ClockIcon, MapPinIcon } from 'lucide-react';
import { Button } from './Button';
type ProgramDetailModalProps = {
  program: any;
  isOpen: boolean;
  onClose: () => void;
};
export function ProgramDetailModal({
  program,
  isOpen,
  onClose
}: ProgramDetailModalProps) {
  const navigate = useNavigate();
  if (!isOpen) return null;
  const handleBookNow = () => {
    navigate('/booking', {
      state: {
        program
      }
    });
    onClose();
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-300">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-301">
        <div className="fixed inset-0 transition-opacity" onClick={onClose} data-id="element-302">
          <div className="absolute inset-0 bg-black opacity-50" data-id="element-303"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-304">
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-305">
          <div className="absolute top-0 right-0 pt-4 pr-4" data-id="element-306">
            <button type="button" className="text-gray-400 hover:text-gray-500 focus:outline-none" onClick={onClose} data-id="element-307">
              <XIcon className="h-6 w-6" data-id="element-308" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-309">
            <div className="h-64 md:h-auto overflow-hidden" data-id="element-310">
              <img src={program.image} alt={program.title} className="w-full h-full object-cover" data-id="element-311" />
            </div>
            <div className="p-6 max-h-[80vh] overflow-y-auto" data-id="element-312">
              <div className="mb-4" data-id="element-313">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#0099A1]/10 text-[#0099A1]" data-id="element-314">
                  {program.category}
                </span>
              </div>
              <h3 className="text-2xl font-bold text-[#374047] mb-2" data-id="element-315">
                {program.title}
              </h3>
              <p className="text-[#374047]/80 mb-6" data-id="element-316">{program.description}</p>
              <div className="space-y-4 mb-6" data-id="element-317">
                <div className="flex items-center" data-id="element-318">
                  <CalendarIcon className="h-5 w-5 text-[#0099A1] mr-3" data-id="element-319" />
                  <div data-id="element-320">
                    <p className="text-sm font-medium text-[#374047]" data-id="element-321">
                      Duration
                    </p>
                    <p className="text-[#374047]/80" data-id="element-322">{program.duration}</p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-323">
                  <DollarSignIcon className="h-5 w-5 text-[#0099A1] mr-3" data-id="element-324" />
                  <div data-id="element-325">
                    <p className="text-sm font-medium text-[#374047]" data-id="element-326">Price</p>
                    <p className="text-[#374047]/80" data-id="element-327">{program.price}</p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-328">
                  <UsersIcon className="h-5 w-5 text-[#0099A1] mr-3" data-id="element-329" />
                  <div data-id="element-330">
                    <p className="text-sm font-medium text-[#374047]" data-id="element-331">
                      Group Size
                    </p>
                    <p className="text-[#374047]/80" data-id="element-332">{program.groupSize}</p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-333">
                  <ClockIcon className="h-5 w-5 text-[#0099A1] mr-3" data-id="element-334" />
                  <div data-id="element-335">
                    <p className="text-sm font-medium text-[#374047]" data-id="element-336">
                      Upcoming Dates
                    </p>
                    <p className="text-[#374047]/80" data-id="element-337">
                      {program.dates.join(', ')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-338">
                  <MapPinIcon className="h-5 w-5 text-[#0099A1] mr-3" data-id="element-339" />
                  <div data-id="element-340">
                    <p className="text-sm font-medium text-[#374047]" data-id="element-341">
                      Location
                    </p>
                    <p className="text-[#374047]/80" data-id="element-342">{program.location}</p>
                  </div>
                </div>
              </div>
              <div className="mb-6" data-id="element-343">
                <h4 className="text-lg font-semibold text-[#374047] mb-2" data-id="element-344">
                  Highlights
                </h4>
                <ul className="space-y-2" data-id="element-345">
                  {program.highlights.map((highlight, index) => <li key={index} className="flex items-start" data-id="element-346">
                      <div className="h-5 w-5 text-[#0099A1] mr-3 flex-shrink-0" data-id="element-347">
                        •
                      </div>
                      <span className="text-[#374047]/80" data-id="element-348">{highlight}</span>
                    </li>)}
                </ul>
              </div>
              {program.hostImage && program.hostName && <div className="mb-6 bg-[#F4E8D0]/30 p-4 rounded-lg" data-id="element-349">
                  <h4 className="text-lg font-semibold text-[#374047] mb-3" data-id="element-350">
                    Your Host
                  </h4>
                  <div className="flex items-start" data-id="element-351">
                    <img src={program.hostImage} alt={program.hostName} className="w-20 h-20 rounded-full object-cover mr-4 flex-shrink-0" data-id="element-352" />
                    <div data-id="element-353">
                      <p className="font-semibold text-[#374047] mb-1" data-id="element-354">
                        {program.hostName}
                      </p>
                      {program.hostBio && <p className="text-sm text-[#374047]/80" data-id="element-355">
                          {program.hostBio}
                        </p>}
                    </div>
                  </div>
                </div>}
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-356">
                <Button variant="primary" onClick={handleBookNow} className="flex-1" data-id="element-357">
                  Book Now
                </Button>
                <Button variant="outline" onClick={onClose} className="flex-1" data-id="element-358">
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}